'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import { BREAKPOINTS } from '@/constants/constants';

import Demo, { DemoSliderControl } from '@/components/Demo';
import InlineToggleControl from '@/components/Controls/InlineToggleControl';

import IconBomb from '../IconBoopDemo/IconBomb';
import useInterval from '@/hooks/use-interval';

function BombExaggeration() {
  const [isAutoplaying, setIsAutoplaying] = React.useState(true);
  const [isBooped, setIsBooped] = React.useState(false);
  const timeoutId = React.useRef<number | null>(null);

  // On mount, check if prefers-reduced-motion is enabled. If so, disable autoplay.
  React.useEffect(() => {
    const QUERY = '(prefers-reduced-motion: no-preference)';
    const prefersReducedMotion = !window.matchMedia(QUERY).matches;

    if (prefersReducedMotion) {
      setIsAutoplaying(false);
    }
  }, []);

  const wrapperSpring = useSpring({
    transform: `rotate(${isBooped ? 10 : 0}deg)`,
    config: {
      tension: 300,
      friction: 10,
    },
  });

  function handleTrigger() {
    setIsBooped(true);

    if (timeoutId.current) {
      window.clearTimeout(timeoutId.current);
    }

    timeoutId.current = window.setTimeout(() => {
      setIsBooped(false);
    }, 150);
  }

  useInterval(handleTrigger, isAutoplaying ? 1500 : null);

  return (
    <Demo
      id="bomb-exaggeration"
      initialValues={{
        magnitude: 50,
      }}
      outputWrapper={null}
      controls={[
        <DemoSliderControl
          id="magnitude"
          key="magnitude"
          label="Magnitude"
          min={0}
          max={130}
          step={5}
        />,
      ]}
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
    >
      {({ magnitude }) => {
        return (
          <>
            <Header>
              <InlineToggleControl
                id="autoplay"
                label="Autoplay animation"
                value={isAutoplaying}
                setValue={(value) => {
                  setIsAutoplaying(value);

                  // Trigger immediately to make clear that the interval has started
                  if (value) {
                    handleTrigger();
                  }
                }}
                handleFocusColor={null}
                handleColor={
                  isAutoplaying
                    ? 'var(--color-text)'
                    : 'var(--color-gray-400)'
                }
              />
            </Header>
            <Wrapper>
              <Button
                style={wrapperSpring}
                onMouseEnter={() => {
                  if (isAutoplaying) {
                    return;
                  }
                  handleTrigger();
                }}
                onPointerDown={() => {
                  if (isAutoplaying) {
                    return;
                  }
                  handleTrigger();
                }}
                onFocus={() => {
                  if (isAutoplaying) {
                    return;
                  }
                  handleTrigger();
                }}
              >
                <IconBomb
                  size={64}
                  isBooped={isBooped}
                  boopRotation={magnitude}
                />
              </Button>
            </Wrapper>
          </>
        );
      }}
    </Demo>
  );
}

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  @media ${BREAKPOINTS.smAndLarger} {
    margin-top: -0.5rem;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  border: 1px solid var(--color-gray-200);
  border-radius: 8px;
  gap: 1rem;
`;

const Button = styled(animated.button)`
  cursor: pointer;
  transform-origin: 37.5% 62.5%;
`;

export default BombExaggeration;
